import { useState, useEffect } from 'react';
import { Box, Typography, Slide, IconButton } from '@mui/material';
import {
  WifiOff as WifiOffIcon,
  Sync as SyncIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useOfflineStore } from '../stores/offlineStore';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

export default function OfflineBanner() {
  const { isOnline, wasOffline } = useNetworkStatus();
  const { pendingSyncCount, syncInProgress } = useOfflineStore();
  const [showReconnected, setShowReconnected] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Show "reconnected" message briefly when coming back online
  useEffect(() => {
    if (wasOffline && isOnline) {
      setShowReconnected(true);
      setDismissed(false);
      const timer = setTimeout(() => {
        setShowReconnected(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [wasOffline, isOnline]);

  // Reset dismissed when going offline again
  useEffect(() => {
    if (!isOnline) {
      setDismissed(false);
    }
  }, [isOnline]);

  const showOffline = !isOnline && !dismissed;
  const showOnline = showReconnected && isOnline && !dismissed;

  if (!showOffline && !showOnline) return null;

  return (
    <Slide direction="down" in={showOffline || showOnline} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          bgcolor: showOffline ? '#115740' : '#2e7d32',
          color: '#fff',
          py: 1,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          minHeight: 48
        }}
      >
        {showOffline ? (
          <>
            <WifiOffIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              You're offline. Changes will be saved and synced when you're back online.
            </Typography>
            {pendingSyncCount > 0 && (
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  px: 1.5,
                  py: 0.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 1
                }}
              >
                <SyncIcon fontSize="small" sx={syncInProgress ? { animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } } : {}} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {pendingSyncCount} pending
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            <SyncIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Back online!{pendingSyncCount > 0 ? ` Syncing ${pendingSyncCount} item${pendingSyncCount !== 1 ? 's' : ''}...` : ' All changes synced.'}
            </Typography>
          </>
        )}
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          sx={{ color: '#fff', ml: 'auto', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Slide>
  );
}
